import { useState } from "react";
import { useIntl } from "react-intl";
import { useAppContext } from "../../app/AppContext";
import type {
  BillingAccountSnapshot,
  BillingCatalogPlan,
  BillingInterval,
  BillingOffer,
  BillingTransition,
} from "../../core/types";
import { InlineApiError } from "../../shared/Shared";
import styles from "./PricingPlans.module.css";

/**
 * The 2026-07-25 design-handoff plans page, one component for both brands —
 * rewritten for the 2026-07-28 commerce cutover: EVERYTHING it renders comes
 * from the server catalog (plans, offers, badges, model chips, feature
 * groups — V5: rendered from the plan constants, no credits) and the account snapshot's server-computed
 * transitions. No plan-id literals, no price comparisons, no derived annual
 * pricing — an arbitrary future catalog renders without client changes.
 */

/** Tone slugs → existing chip classes; unknown slugs fall back to plain. */
const TONE_CLASSES: Record<string, string> = {
  info: "toneInfo",
  primary: "tonePrimary",
  "primary-deep": "tonePrimaryDeep",
  accent: "toneAccent",
};

function toneClass(base: string, tone: string | null): string {
  const modifier = tone ? TONE_CLASSES[tone] : undefined;
  const moduleClass = modifier ? styles[modifier] : undefined;
  return moduleClass ? `${base} ${moduleClass}` : base;
}

function offerFor(plan: BillingCatalogPlan, period: BillingInterval): BillingOffer | null {
  return (
    plan.offers.find((offer) => offer.interval === period) ??
    plan.offers.find((offer) => offer.interval === "month") ??
    plan.offers[0] ??
    null
  );
}

export function PricingPlans({
  plans,
  snapshot,
  signedOut,
  busy,
  error,
  onSelect,
}: {
  plans: BillingCatalogPlan[];
  /** null while signed out or the account snapshot is still loading. */
  snapshot: BillingAccountSnapshot | null;
  signedOut: boolean;
  busy: boolean;
  error: unknown;
  onSelect(offer: BillingOffer, plan: BillingCatalogPlan): void;
}) {
  const intl = useIntl();
  const { experience } = useAppContext();
  const isMfa = experience.id === "myfirstagent";
  const suffix = isMfa ? "mfa" : "lya";
  const [period, setPeriod] = useState<BillingInterval>("month");
  const hasYearOffers = plans.some((plan) =>
    plan.offers.some((offer) => offer.interval === "year"),
  );
  const transitionsByOffer = new Map<string, BillingTransition>(
    (snapshot?.availableTransitions ?? []).map((transition) => [transition.offerId, transition]),
  );
  return (
    <div className={styles.pricingPage}>
      <header className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>{intl.formatMessage({ id: "pricing.title" })}</h1>
        <p className={styles.pageSubtitle}>
          {intl.formatMessage({ id: `pricing.subhead.${suffix}.plans` })}
        </p>
      </header>

      <InlineApiError error={error} />

      {hasYearOffers ? (
        // biome-ignore lint/a11y/useSemanticElements: the billing toggle is a two-button pill group per the handoff markup, not a form fieldset
        <div
          className={styles.billingToggle}
          role="group"
          aria-label={intl.formatMessage({ id: "pricing.billing.label" })}
        >
          <button
            type="button"
            className={
              period === "month" ? `${styles.toggleBtn} ${styles.isSelected}` : styles.toggleBtn
            }
            aria-pressed={period === "month"}
            onClick={() => setPeriod("month")}
          >
            {intl.formatMessage({ id: "pricing.billing.monthly" })}
          </button>
          <button
            type="button"
            className={
              period === "year" ? `${styles.toggleBtn} ${styles.isSelected}` : styles.toggleBtn
            }
            aria-pressed={period === "year"}
            onClick={() => setPeriod("year")}
          >
            {intl.formatMessage({ id: "pricing.billing.annual" })}
          </button>
        </div>
      ) : null}

      <div className={styles.planGrid}>
        {plans.map((plan) => {
          const presentation = plan.presentation;
          const offer = offerFor(plan, period);
          const featured = presentation.badges.some((badge) => badge.slug === "most_popular");
          const isCurrentPlan = snapshot !== null && snapshot.plan.id === plan.id;
          // An admin's test plan (planSource admin_override) is CURRENT for the badge but
          // never OWNED: every paid offer stays purchasable, and buying creates the live
          // subscription row that wins by precedence.
          const isTesting = isCurrentPlan && snapshot?.planSource === "admin_override";
          // Offer-exact current: the entitled offer, or the whole plan when
          // free access has no entitled offer to compare against.
          const isCurrentOffer =
            isCurrentPlan &&
            !isTesting &&
            (snapshot?.entitledOffer === null || snapshot?.entitledOffer?.id === offer?.id);
          const transition = offer ? transitionsByOffer.get(offer.id) : undefined;
          // The server decides commerce availability; paid CTAs render as
          // "coming soon" instead of issuing requests that can only fail.
          const commerceOff =
            snapshot?.commerceAvailable === false && offer !== null && offer.amountCents > 0;

          let ctaLabel: string;
          let ctaDisabled = busy || offer === null;
          if (signedOut) {
            ctaLabel =
              offer !== null && offer.amountCents === 0
                ? intl.formatMessage({ id: "pricing.cta.start" })
                : intl.formatMessage({ id: "pricing.cta.get" }, { name: presentation.name });
          } else if (isCurrentOffer) {
            ctaLabel = intl.formatMessage({ id: "pricing.cta.current" });
            ctaDisabled = true;
          } else if (commerceOff) {
            ctaLabel = intl.formatMessage({ id: "pricing.cta.unavailable" });
            ctaDisabled = true;
          } else if (transition) {
            ctaLabel =
              transition.kind === "upgrade"
                ? intl.formatMessage({ id: "pricing.cta.upgrade" })
                : transition.kind === "downgrade"
                  ? intl.formatMessage({ id: "pricing.cta.downgrade" })
                  : transition.kind === "cadence_change"
                    ? intl.formatMessage({
                        id:
                          offer?.interval === "year"
                            ? "pricing.cta.switchAnnual"
                            : "pricing.cta.switchMonthly",
                      })
                    : transition.kind === "move_to_free"
                      ? intl.formatMessage({ id: "pricing.cta.free" })
                      : intl.formatMessage({ id: "pricing.cta.get" }, { name: presentation.name });
          } else {
            // Signed in but the server offered no transition to this offer.
            ctaLabel = intl.formatMessage({ id: "pricing.cta.get" }, { name: presentation.name });
            ctaDisabled = true;
          }

          const isDowngradeCta =
            transition?.kind === "downgrade" || transition?.kind === "move_to_free";
          const cardClass = [
            styles.planCard,
            featured ? styles.isFeatured : "",
            isCurrentPlan ? styles.isCurrent : "",
          ]
            .filter(Boolean)
            .join(" ");
          // A downgrade never gets the featured primary treatment.
          const ctaClass = isCurrentOffer
            ? `${styles.ctaBtn} ${styles.isCurrent}`
            : featured && !isDowngradeCta
              ? `${styles.ctaBtn} ${styles.isPrimary}`
              : styles.ctaBtn;

          return (
            <article
              key={plan.id}
              className={cardClass}
              aria-current={isCurrentPlan ? "true" : undefined}
            >
              {presentation.badges.map((badge) => (
                <p
                  key={badge.slug}
                  className={
                    badge.slug === "most_popular"
                      ? styles.planRibbon
                      : badge.slug === "start_here"
                        ? `${styles.planRibbon} ${styles.badgeStart}`
                        : `${styles.planRibbon} ${styles.badgePlain}`
                  }
                >
                  {badge.label}
                </p>
              ))}
              {isTesting ? (
                <p className={`${styles.planRibbon} ${styles.badgePlain}`}>
                  {intl.formatMessage({ id: "pricing.badge.testing" })}
                </p>
              ) : null}
              <h2 className={styles.planName}>{presentation.name}</h2>
              <p className={styles.planTagline}>{presentation.blurb}</p>
              <div className={styles.priceBlock}>
                {offer === null || offer.amountCents === 0 ? (
                  <>
                    <div className={styles.priceRow}>
                      <span className={styles.priceAmount}>
                        {intl.formatMessage({ id: "pricing.free" })}
                      </span>
                    </div>
                    <p className={styles.priceNote}>
                      {offer?.note ?? intl.formatMessage({ id: "pricing.freeNote" })}
                    </p>
                  </>
                ) : (
                  <>
                    <div className={styles.priceRow}>
                      <span className={styles.priceCurrency}>US$</span>
                      <span className={styles.priceAmount}>
                        {intl.formatNumber(offer.amountCents / 100)}
                      </span>
                      <span className={styles.priceUnit}>
                        {offer.interval === "month"
                          ? intl.formatMessage({ id: "planGrid.perMonth" })
                          : intl.formatMessage({ id: "pricing.perYear" })}
                      </span>
                    </div>
                    <p
                      className={
                        offer.note ? `${styles.priceNote} ${styles.isGood}` : styles.priceNote
                      }
                    >
                      {offer.note ??
                        (offer.interval === "month"
                          ? intl.formatMessage({ id: "pricing.billedMonthly" })
                          : "")}
                    </p>
                  </>
                )}
              </div>
              <button
                type="button"
                className={ctaClass}
                disabled={ctaDisabled}
                onClick={() => {
                  if (offer) onSelect(offer, plan);
                }}
              >
                {ctaLabel}
              </button>
              {presentation.modelHighlights.length > 0 ? (
                <>
                  <p className={styles.modelsLabel}>
                    {intl.formatMessage({ id: "pricing.models.label" })}
                  </p>
                  <ul className={styles.modelChips}>
                    {presentation.modelHighlights.map((chip) => (
                      <li key={chip.label} className={toneClass(styles.modelChip, chip.tone)}>
                        {chip.label}
                      </li>
                    ))}
                  </ul>
                </>
              ) : null}
              <ul className={styles.featureList}>
                {presentation.comparisonLeadIn ? (
                  <li className={styles.featureLead}>{presentation.comparisonLeadIn}</li>
                ) : null}
                {presentation.featureGroups.map((group, groupIndex) => (
                  <li key={group.title ?? groupIndex}>
                    {group.title ? <p className={styles.featureLead}>{group.title}</p> : null}
                    <ul className={styles.featureList}>
                      {group.items.map((item) => (
                        <li key={item} className={styles.featureItem}>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            </article>
          );
        })}
      </div>

      {isMfa ? null : (
        <aside className={styles.districtStrip}>
          <div className={styles.districtIntro}>
            <h2 className={styles.districtTitle}>
              {intl.formatMessage({ id: "pricing.district.title" })}
            </h2>
            <p className={styles.districtBody}>
              {intl.formatMessage({ id: "pricing.district.body" })}
            </p>
          </div>
          <a className={styles.districtCta} href="mailto:hello@launchyouragent.ai">
            {intl.formatMessage({ id: "pricing.district.cta" })}
          </a>
        </aside>
      )}

      <p className={styles.pageFootnote}>
        {intl.formatMessage({ id: `pricing.footnote.${suffix}` })}
      </p>
    </div>
  );
}
